// Imports => Vendor
import axios from 'axios';

// Imports => MOBX
import { makeObservable, observable, computed, action } from 'mobx';

// Imports => Constants
import { KEYS } from '@opp/ui-kit';

// Imports => Utilities
import {
  AcSanitize,
  AcSaveState,
  AcRemoveState,
  AcIsSet,
  AcIsNull,
  AcFormatErrorMessage,
  AcFormatErrorCode,
  AcIsUndefined,
  AcFormatRequestParameters,
} from '@opp/ui-kit';
import { AcAutoLoad, AcAutoSave } from '@utils';

const _default = {
  options: {},
  tickets: null,
};

let app = {};

export class TicketsStore {
  constructor(store) {
    makeObservable(this);

    AcAutoLoad(this, KEYS.TICKETS);
    AcAutoSave(this);

    app.store = store;
  }

    @observable
      tickets = _default.tickets;

    @computed
    get current_tickets() {
      return this.tickets;
    }

    @observable
      loading = {
        status: false,
        message: null,
      };

    @computed
    get is_loading() {
      return this.loading.status;
    }

    @action
      setLoading = (state, message) => {
        this.loading = {
          status: state || false,
          message,
        };
      };

    @observable
      busy = {
        status: false,
        message: null,
      };

    @computed
    get is_busy() {
      return this.busy.status;
    }

    @action
      setBusy = (state, message) => {
        this.busy = {
          status: state || false,
          message,
        };
      };

    @observable
      order_by = {
        field: null,
        direction: KEYS.ASCENDING,
      };

    @observable
      page = 1;

    @observable
      perpage = 25;

    @observable
      query = null;

    @action
      setQuery = (input) => {
        const _input = AcSanitize(input);
        if (this.current_query !== _input) this.query = _input;
      };

    @action
      setPageNumber = (num) => {
        if (this.page !== num) this.page = num;
      };

    @action
      setPerPage = (num) => {
        if (this.perpage !== num) this.perpage = num;
      };

    @action
      setOrderBy = (field, direction = null) => {
        let order_by = this.order_by;

        if (AcIsSet(direction)) {
          order_by.direction = direction;
        } else if (order_by.field === field) {
          order_by.direction =
                order_by.direction === KEYS.ASCENDING
                  ? KEYS.DESCENDING
                  : KEYS.ASCENDING;
        } else order_by.direction = KEYS.ASCENDING;

        order_by.field = field;

        this.order_by = order_by;
        this.setPageNumber(1);
      };

    @computed
    get current_page() {
      return this.page;
    }

    @computed
    get current_per_page() {
      return this.perpage;
    }

    @computed
    get current_order_by() {
      return this.order_by;
    }

    @computed
    get current_query() {
      return this.query;
    }

    @action
      resetParams = () => {
        this.page = 1;
        this.query = null;
        this.perpage = 25;
        this.order_by = { field: null, direction: KEYS.ASCENDING };
      };

    @action
      list = (t, options = {}) => {
        this.setLoading(true);

        const params = AcFormatRequestParameters(this, options);

        return app.store.api.tickets
          .list(params)
          .then(async (response) => {
            await this.set(KEYS.TICKETS, response);

            this.setLoading(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              app.store.toasters.add({
                variant: 'error',
                title: t('tickets.failed-to-retrieve', {ns: 'store'}),
                description: AcFormatErrorMessage(error),
                code: AcFormatErrorCode(error),
              });
            }

            this.setLoading(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      store = (t, data) => {
        this.setBusy(true);

        return app.store.api.tickets
          .store(data)
          .then(async (response) => {
            await this.list({ perpage: 999 });

            app.store.toasters.add({
              variant: 'success',
              title: t('tickets.save-success', {ns: 'store'}),
            });

            this.setBusy(false);

            return response;
          })
          .catch((error) => {
            if (!axios.isCancel(error)) {
              let message = t('tickets.save-failed', {ns: 'store'});

              if (error.response && error.response.data && error.response.data.core && error.response.data.core.error && error.response.data.core.error.message) {
                message = error.response.data.core.error.message;
              }

              if (error.response && error.response.data && error.response.data.core && error.response.data.core.error && error.response.data.core.error.parameters) {
                message = Object.values(error.response.data.core.error.parameters)[0];
              }
              app.store.toasters.add({
                variant: 'error',
                title: message,
                description: AcFormatErrorMessage(error),
              });
            }

            this.setBusy(false);

            if (!axios.isCancel(error)) throw error;
          });
      };

    @action
      set = (target, value, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (AcIsUndefined(value)) return;

        return new Promise((resolve) => {
          this[target] = value;
          if (save) AcSaveState(target, value);
          resolve();
        });
      };

    @action
      setState = (target, property, value, save) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;
        if (!AcIsSet(property)) return;
        if (AcIsUndefined(value)) return;

        this[target][property] = value;
        if (save) AcSaveState(target, value);
      };

    @action
      reset = (target, save = false) => {
        if (!AcIsSet(target)) return;
        if (AcIsUndefined(this[target])) return;

        return new Promise((resolve) => {
          this[target] = _default[target];
          if (save && AcIsNull(_default[target])) {
            AcRemoveState(target);
          } else if (save) {
            AcSaveState(target, _default[target]);
          }

          resolve();
        });
      };
}

export default TicketsStore;
